import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import ValidationService from "@/services/ValidationService";

const maxSize5MB = (value) => !value || value.size <= 5 * 1024 * 1024;

export default {
  name: "TimetableImport",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },

  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      disableImportBtn: false,
      disableConfirmBtn: false,
      disableInputFields: false,
      disableAcademicYear: false,
      submitted: false,
      templateSubmitted: false,
      importStep: "form",
      periodArr: [],
      template_academic_year_id: null,
      academic_year_id: null,
      file_name: "",
      selectedFile: null,
      invalidFileType: false,
      previewRows: [],
      previewTotal: 0,
      previewEditIndex: null,
      previewEditSnapshot: null,
    };
  },

  validations: {
    academic_year_id: { required },
    selectedFile: { required, maxSize5MB },
  },

  computed: {
    disabledTemplateBtn() {
      return this.disableInputFields;
    },

    selectedAcademicYearName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.academic_year_id)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },

    isPreviewStep() {
      return this.importStep === "preview" || this.importStep === "previewEdit";
    },

    selectedPreviewRow() {
      if (this.previewEditIndex === null || !this.previewRows[this.previewEditIndex]) {
        return null;
      }

      return this.previewRows[this.previewEditIndex];
    },
  },

  methods: {
    getAcademicYear() {
      this.isLoading = true;
      axios.get("period/periodList")
        .then((res) => {
          this.periodArr = res.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }
      this.$refs.fileUploader.value = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["xls", "xlsx"].includes(extension)) {
        this.invalidFileType = true;
        return;
      }

      this.selectedFile = file;
      this.file_name = file.name;
    },

    getTemplateFileName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.template_academic_year_id)
      );
      const periodName = (selected?.text || "all").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_timetable_${year}_${month}_${day}.xlsx`;
    },

    downloadTemplate() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      this.isLoading = true;
      const params = {};
      if (this.template_academic_year_id) {
        params.academic_year_id = this.template_academic_year_id;
      }

      axios.get("timetable/excelTemplateDownload", {
        params,
        responseType: "blob",
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");

          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          this.handleTemplateDownloadError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || this.invalidFileType) {
        return;
      }

      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.disableInputFields = true;
      this.disableAcademicYear = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.academic_year_id);
      formData.append("file", this.selectedFile);

      axios.post("timetable/excelImportValidate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          this.previewRows = this.clonePreviewRows(response.data.preview || []);
          this.previewTotal = response.data.total || this.previewRows.length;
          this.previewEditIndex = null;
          this.previewEditSnapshot = null;
          this.clearImportConfirmErrors();
          this.importStep = "preview";
        })
        .catch((error) => {
          this.handleImportValidationError(error);
          this.disableInputFields = false;
          this.disableImportBtn = false;
          this.disableAcademicYear = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImportSubmit() {
      this.clearImportConfirmErrors();

      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;

      axios.post("timetable/excelImportConfirm", {
        academic_year_id: this.academic_year_id,
        preview: this.buildConfirmPreviewPayload(),
      })
        .then((response) => {
          this.$notification.success(response.data.message || "Timetables imported successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.$router.push({ name: "timetableList" });
        })
        .catch((error) => {
          this.handleConfirmBackendError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.disableConfirmBtn = false;
        });
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row) => ({
        class_id: row.class_id,
        class_name: row.class_name || "",
        academic_year_name: row.academic_year_name || "",
        template_id: row.template_id,
        template_name: row.template_name || "",
        filled_slot_count: row.filled_slot_count,
        total_slot_count: row.total_slot_count,
        days: Array.isArray(row.days) ? row.days.map((day) => ({ ...day })) : [],
        times: Array.isArray(row.times) ? row.times.map((time) => ({ ...time })) : [],
        items: Array.isArray(row.items)
          ? row.items.map((item) => ({
            day_id: item.day_id,
            time_id: item.time_id,
            subject_id: item.subject_id,
            subject_name: String(item.subject_name || "").trim(),
            teacher_name: String(item.teacher_name || "").trim(),
            teacher_no: String(item.teacher_no || "").trim(),
            subject_teacher_id: item.subject_teacher_id ?? null,
            day_name: item.day_name || "",
            time_name: item.time_name || "",
          }))
          : [],
      }));
    },

    buildConfirmPreviewPayload() {
      return this.previewRows.map((row) => ({
        class_id: row.class_id,
        class_name: row.class_name,
        template_id: row.template_id,
        template_name: row.template_name,
        items: (row.items || []).map((item) => ({
          day_id: item.day_id,
          time_id: item.time_id,
          subject_name: String(item.subject_name || "").trim(),
          teacher_name: this.formatTeacherDisplay(item),
          subject_teacher_id: item.subject_teacher_id ?? null,
          day_name: item.day_name,
          time_name: item.time_name,
        })),
      }));
    },

    validatePreviewBeforeConfirm() {
      const errors = {};

      this.previewRows.forEach((row, classRowIndex) => {
        const rowErrors = this.validateSinglePreviewRow(classRowIndex);
        if (Object.keys(rowErrors).length) {
          errors[classRowIndex] = rowErrors;
        }
      });

      return errors;
    },

    /**
     * Validate one preview timetable row before update or confirm.
     *
     * @param {number} classRowIndex
     * @returns {Object<string, string>}
     */
    validateSinglePreviewRow(classRowIndex) {
      const row = this.previewRows[classRowIndex];
      if (!row) {
        return {};
      }

      const rowErrors = {};
      const className = String(row.class_name || "").trim();
      const items = Array.isArray(row.items) ? row.items : [];
      const expectedSlotCount = Number(row.total_slot_count) || 0;
      let filledCount = 0;

      if (!className) {
        rowErrors._row = "Class name is required.";
      }

      if (expectedSlotCount > 0 && items.length !== expectedSlotCount) {
        rowErrors._row = rowErrors._row || "Invalid number of timetable slots.";
        return rowErrors;
      }

      const seenSlots = {};

      items.forEach((item, itemIndex) => {
        const slotKey = `slot_${itemIndex}`;
        const dayId = item.day_id;
        const timeId = item.time_id;
        const subjectName = String(item.subject_name || "").trim();
        const teacherName = String(item.teacher_name || "").trim();
        const dayTimeKey = `${dayId}-${timeId}`;

        if (dayId == null || timeId == null) {
          rowErrors[slotKey] = "Invalid day/time slot.";
          return;
        }

        if (seenSlots[dayTimeKey]) {
          rowErrors[slotKey] = "Duplicate day/time slot.";
          return;
        }

        seenSlots[dayTimeKey] = true;

        if (subjectName === "" && teacherName !== "") {
          rowErrors[slotKey] = "Teacher cannot be set without a subject.";
          return;
        }

        if (subjectName !== "") {
          filledCount++;
        }
      });

      if (filledCount < 1 && items.length > 0) {
        const firstEmptyIndex = items.findIndex(
          (item) => !String(item.subject_name || "").trim()
        );
        if (firstEmptyIndex !== -1) {
          rowErrors[`slot_${firstEmptyIndex}`] = "At least one subject required.";
        }
      }

      return rowErrors;
    },

    recalculateFilledSlotCount(row) {
      const items = Array.isArray(row?.items) ? row.items : [];
      row.filled_slot_count = items.filter(
        (item) => String(item.subject_name || "").trim() !== ""
      ).length;
    },

    getSlotFieldError(classRowIndex, dayId, timeId) {
      const row = this.previewRows[classRowIndex];
      if (!row || !Array.isArray(row.items)) {
        return "";
      }

      const itemIndex = row.items.findIndex(
        (item) => String(item.day_id) === String(dayId) && String(item.time_id) === String(timeId)
      );

      if (itemIndex === -1) {
        return "";
      }

      return this.getFieldError(classRowIndex, `slot_${itemIndex}`);
    },

    getSlotItem(dayId, timeId) {
      const row = this.selectedPreviewRow;
      if (!row || !Array.isArray(row.items)) {
        return null;
      }

      return row.items.find(
        (item) => String(item.day_id) === String(dayId) && String(item.time_id) === String(timeId)
      ) || null;
    },

    formatTeacherDisplay(item) {
      const teacherName = String(item?.teacher_name || "").trim();
      const teacherNo = String(item?.teacher_no || "").trim();
      if (!teacherName) {
        return "";
      }
      if (!teacherNo) {
        return teacherName;
      }
      return `${teacherName} (${teacherNo})`;
    },

    onPreviewSubjectInput(dayId, timeId, value) {
      const item = this.getSlotItem(dayId, timeId);
      if (item) {
        item.subject_name = value;
        item.subject_id = null;
        item.subject_teacher_id = null;
        if (!String(value || "").trim()) {
          item.teacher_name = "";
          item.teacher_no = "";
        }
      }

      if (this.previewEditIndex === null) {
        return;
      }

      const row = this.previewRows[this.previewEditIndex];
      if (!row || !Array.isArray(row.items)) {
        return;
      }

      const itemIndex = row.items.findIndex(
        (slotItem) => String(slotItem.day_id) === String(dayId) && String(slotItem.time_id) === String(timeId)
      );

      if (itemIndex !== -1) {
        this.onPreviewFieldInput(this.previewEditIndex, `slot_${itemIndex}`);
      }
    },

    onPreviewTeacherInput(dayId, timeId, value) {
      const item = this.getSlotItem(dayId, timeId);
      if (item) {
        const parsed = this.parseTeacherInput(value);
        item.teacher_name = parsed.teacher_name;
        item.teacher_no = parsed.teacher_no;
        item.subject_teacher_id = null;
      }

      if (this.previewEditIndex === null) {
        return;
      }

      const row = this.previewRows[this.previewEditIndex];
      if (!row || !Array.isArray(row.items)) {
        return;
      }

      const itemIndex = row.items.findIndex(
        (slotItem) => String(slotItem.day_id) === String(dayId) && String(slotItem.time_id) === String(timeId)
      );

      if (itemIndex !== -1) {
        this.onPreviewFieldInput(this.previewEditIndex, `slot_${itemIndex}`);
      }
    },

    parseTeacherInput(value) {
      const trimmed = String(value || "").trim();
      if (!trimmed) {
        return { teacher_name: "", teacher_no: "" };
      }
      const match = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
      if (match) {
        return {
          teacher_name: match[1].trim(),
          teacher_no: match[2].trim(),
        };
      }
      return { teacher_name: trimmed, teacher_no: "" };
    },

    handleImportValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.message || "Import validation failed."];

        window.alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }

      this.handleError(error);
    },

    setConfirmFieldErrors(fieldErrors) {
      this.confirmFieldErrors = this.resolveTimetableSlotFieldErrors(fieldErrors || {});
    },

    /**
     * Map subject_name / teacher_name field errors to timetable slot keys for inline grid display.
     *
     * @param {Object<string, Object<string, string>>} fieldErrors
     * @returns {Object<string, Object<string, string>>}
     */
    resolveTimetableSlotFieldErrors(fieldErrors) {
      const resolved = {};

      Object.entries(fieldErrors || {}).forEach(([rowIndex, fields]) => {
        if (!fields || typeof fields !== "object") {
          return;
        }

        const rowFields = { ...fields };
        const row = this.previewRows[rowIndex];
        const items = Array.isArray(row?.items) ? row.items : [];

        ["subject_name", "teacher_name"].forEach((fieldKey) => {
          const fieldError = rowFields[fieldKey];
          if (!fieldError) {
            return;
          }

          const itemMatch = String(fieldError).match(/\bat item\s+(\d+)\b/i);
          let itemIndex = itemMatch ? Math.max(0, parseInt(itemMatch[1], 10) - 1) : -1;

          if (itemIndex < 0 || itemIndex >= items.length) {
            if (fieldKey === "subject_name") {
              const nameMatch = String(fieldError).match(/Subject Name\s*'([^']+)'/i);
              const subjectToFind = nameMatch ? nameMatch[1].trim().toLowerCase() : "";
              if (subjectToFind) {
                itemIndex = items.findIndex(
                  (item) => String(item.subject_name || "").trim().toLowerCase() === subjectToFind
                );
              }
            }

            if (fieldKey === "teacher_name") {
              const nameMatch = String(fieldError).match(/Teacher(?:\s*name)?\s*'([^']+)'/i);
              const teacherToFind = nameMatch ? nameMatch[1].trim().toLowerCase() : "";
              if (teacherToFind) {
                itemIndex = items.findIndex(
                  (item) => String(item.teacher_name || "").trim().toLowerCase() === teacherToFind
                );
              }
            }
          }

          if (itemIndex < 0) {
            itemIndex = items.findIndex((item) => {
              if (fieldKey === "teacher_name") {
                return String(item.teacher_name || "").trim() !== "";
              }
              return String(item.subject_name || "").trim() !== "";
            });
          }

          if (itemIndex !== -1) {
            rowFields[`slot_${itemIndex}`] = fieldError;
          }

          delete rowFields[fieldKey];
        });

        resolved[rowIndex] = rowFields;
      });

      return resolved;
    },

    getImportConfirmErrorOptions() {
      return { openTimetableEditOnError: true };
    },

    showPreviewEdit(index) {
      this.previewEditIndex = index;
      const cloned = this.clonePreviewRows([this.previewRows[index]]);
      this.previewEditSnapshot = cloned[0] || null;
      this.importStep = "previewEdit";
    },

    updatePreviewEdit() {
      const index = this.previewEditIndex;
      if (index === null) {
        return;
      }

      const rowErrors = this.validateSinglePreviewRow(index);
      if (Object.keys(rowErrors).length) {
        this.$set(this.confirmFieldErrors, index, rowErrors);
        return;
      }

      this.$delete(this.confirmFieldErrors, index);
      this.recalculateFilledSlotCount(this.previewRows[index]);
      this.previewEditSnapshot = null;
      this.previewEditIndex = null;
      this.importStep = "preview";
    },

    cancelPreviewEdit() {
      const index = this.previewEditIndex;
      if (index !== null && this.previewEditSnapshot) {
        this.$set(this.previewRows, index, this.previewEditSnapshot);
        this.$delete(this.confirmFieldErrors, index);
      }

      this.previewEditSnapshot = null;
      this.previewEditIndex = null;
      this.importStep = "preview";
    },

    handleTemplateDownloadError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data;
        if (data instanceof Blob) {
          data.text().then((text) => {
            try {
              const json = JSON.parse(text);
              const messages = json.errors?.length
                ? json.errors
                : [json.message || "Template download failed."];
              alert(messages.join("\n"));
            } catch (parseError) {
              alert("Template download failed.");
            }
          });
          return;
        }
        this.handleImportValidationError(error);
        return;
      }

      this.handleError(error);
    },

    handleValidationError(error) {
      this.handleImportValidationError(error);
    },

    resetForm() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.importStep = "form";
      this.disableInputFields = false;
      this.disableAcademicYear = false;
      this.disableImportBtn = false;
      this.disableConfirmBtn = false;
      this.previewRows = [];
      this.previewTotal = 0;
      this.previewEditIndex = null;
      this.previewEditSnapshot = null;
      this.clearImportConfirmErrors();
      this.template_academic_year_id = null;
      this.academic_year_id = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$v.$reset();
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYear();
    },

    handleError(error) {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message = error.response?.data?.message || "Something went wrong.";
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },
  },

  mounted() {
    this.getAcademicYear();
  },
};
